// SCR-STAFF-407 — برآورد هزینه (US-BIL-080 تا 083؛ FLOW-12): فهرست با وضعیت، ایجاد چندگزینه‌ای (حداکثر ۳ — AC-BIL-081.1) از انتخاب دستی یا
// شبیه‌ساز CAT (C-AGENT-704)، ارائه با PDF و قفل قیمت، چاپ، ارسال برای امضای از راه دور یا تأیید با OTP موبایل اصلی (C-AGENT-824)، رد و تبدیل
// برآورد پذیرفته به پکیج، قیمت قفل‌شده و پیشنهاد نوبت مراحل (C-AGENT-825).
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type BilConvertQuoteResponse,
  type BilCreateQuoteRequest,
  type BilQuoteListItem,
  type BilQuoteStatus,
  type BilQuoteView,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, JalaliDate, ListPage, LoadingState, PageHeader, ReasonDialog, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useActiveServices } from '../catalog/common';
import { PatientPicker } from '../patients/PatientPicker';
import { parseCodes } from './PackagesPage';
import { Amount, bilKeys, cashierPatientPath, newQuotePath, num, packagesPath, quotePath, QuoteStatusChip, saveBlob, SCR_STAFF_407 } from './common';

const STATUSES: readonly BilQuoteStatus[] = ['Draft', 'Presented', 'Accepted', 'Rejected', 'Expired', 'Converted'];
const MAX_OPTIONS = 3;

/** سبد ورودی از شبیه‌ساز قیمت CAT (state مسیر). */
export interface QuoteDraftState {
  source: 'Simulator';
  lines: { serviceId: string | null; packageId: string | null; quantity: number }[];
  codes: string[];
}

export function QuotesPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const navigate = useNavigate();
  const [status, setStatus] = useState<BilQuoteStatus | ''>('');
  const quotes = useInfiniteQuery({
    queryKey: bilKeys.quotes(session?.tenantId, status),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(tenantApi.GET('/api/v1/billing/quotes', { params: { query: { status: status || undefined, cursor: pageParam, limit: 50 } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const columns: DataTableColumn<BilQuoteListItem>[] = [
    {
      id: 'number',
      headerKey: 'bil:quotes.columns.number',
      cell: (row) => (
        <RouterLink to={quotePath(row.id)} data-testid="bil-quote-link">
          {row.number}
        </RouterLink>
      ),
    },
    { id: 'patient', headerKey: 'bil:quotes.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'amount', headerKey: 'bil:quotes.columns.amount', cell: (row) => <Amount money={row.amount} /> },
    { id: 'status', headerKey: 'bil:quotes.columns.status', cell: (row) => <QuoteStatusChip status={row.status} /> },
    { id: 'valid', headerKey: 'bil:quotes.columns.valid_until', cell: (row) => <JalaliDate value={row.validUntil} /> },
    { id: 'source', headerKey: 'bil:quotes.columns.source', cell: (row) => t(`bil:quote_source.${row.source}`) },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_407}
      titleKey="bil:quotes.title"
      subtitleKey="bil:quotes.subtitle"
      actions={
        access.accessMode === 'Full' && access.can('bil.quote.create') ? (
          <Button variant="contained" onClick={() => void navigate(newQuotePath())} data-testid="bil-quote-new">
            + {t('bil:quotes.new')}
          </Button>
        ) : undefined
      }
      filters={
        <TextField select size="small" label={t('bil:quotes.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as BilQuoteStatus | '')} sx={{ minWidth: 200 }}>
          <MenuItem value="">{t('bil:invoices.all')}</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`bil:quote_status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <DataTable
        captionKey="bil:quotes.title"
        columns={columns}
        rows={quotes.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={quotes.isPending}
        error={quotes.error}
        onRetry={() => void quotes.refetch()}
        hasMore={quotes.hasNextPage}
        onLoadMore={() => void quotes.fetchNextPage()}
        isFetchingMore={quotes.isFetchingNextPage}
        emptyTitleKey="bil:quotes.empty"
      />
    </ListPage>
  );
}

interface EditorLine {
  key: number;
  kind: 'Service' | 'Package';
  serviceId: string | null;
  packageId: string | null;
  quantity: string;
  stage: string;
}

interface EditorOption {
  key: number;
  title: string;
  lines: EditorLine[];
}

let editorSequence = 0;
const nextKey = () => {
  editorSequence += 1;
  return editorSequence;
};
const blankLine = (kind: 'Service' | 'Package' = 'Service'): EditorLine => ({ key: nextKey(), kind, serviceId: null, packageId: null, quantity: '1', stage: '1' });

/** ایجاد برآورد (AC-BIL-080.1): مراجعه‌کننده، تا ۳ گزینه، اقلام خدمت (تعداد جلسه و مرحله) یا پکیج، کوپن و شرایط. */
export function QuoteEditorPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const key = useIdempotencyKey();
  const services = useActiveServices();
  const draft = (location.state as { quoteDraft?: QuoteDraftState } | null)?.quoteDraft;
  const [patientId, setPatientId] = useState(params.get('patientId') ?? '');
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [options, setOptions] = useState<EditorOption[]>(() => [
    {
      key: nextKey(),
      title: t('bil:quotes.option_default', { letter: t('bil:quotes.letters.0') }),
      lines: draft?.lines.length
        ? draft.lines.map((line) => ({
            key: nextKey(),
            kind: line.packageId ? 'Package' : 'Service',
            serviceId: line.serviceId,
            packageId: line.packageId,
            quantity: String(line.quantity),
            stage: '1',
          }))
        : [blankLine()],
    },
  ]);
  const [recommended, setRecommended] = useState(0);
  const [codesText, setCodesText] = useState(draft?.codes.join(' ') ?? '');
  const [notes, setNotes] = useState('');
  const [validityDays, setValidityDays] = useState('30');
  const packages = useQuery({
    queryKey: bilKeys.sellable(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/packages', { params: { query: { branchId: session?.activeBranchId ?? undefined } } })),
  });
  const packageOptions = (packages.data?.items ?? []).filter((item) => item.supported);
  const updateLine = (optionKey: number, lineKey: number, change: Partial<EditorLine>) =>
    setOptions((current) =>
      current.map((option) => (option.key === optionKey ? { ...option, lines: option.lines.map((line) => (line.key === lineKey ? { ...line, ...change } : line)) } : option)),
    );
  const body = (): BilCreateQuoteRequest => ({
    patientId,
    branchId: session?.activeBranchId ?? null,
    source: draft ? 'Simulator' : 'Manual',
    paymentTerms: 'Cash',
    validityDays: Number(validityDays) || null,
    notes: notes.trim() || null,
    codes: parseCodes(codesText),
    options: options.map((option, index) => ({
      title: option.title.trim() || t('bil:quotes.option_default', { letter: t(`bil:quotes.letters.${index}`) }),
      recommended: index === recommended,
      lines: option.lines
        .filter((line) => (line.kind === 'Service' ? line.serviceId : line.packageId))
        .map((line) => ({
          serviceId: line.kind === 'Service' ? line.serviceId : null,
          packageId: line.kind === 'Package' ? line.packageId : null,
          quantity: Math.max(1, Number(line.quantity) || 1),
          stage: Math.max(1, Number(line.stage) || 1),
          providerId: null,
        })),
    })),
  });
  const create = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/billing/quotes', { body: body(), headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (quote) => void navigate(quotePath(quote.id)),
  });
  const valid = patientId !== '' && options.every((option) => option.lines.some((line) => (line.kind === 'Service' ? line.serviceId : line.packageId)));
  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_407}>
      <PageHeader titleKey="bil:quotes.new" subtitleKey={draft ? 'bil:quotes.from_simulator' : undefined} />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          {params.get('patientId') && !patient ? (
            <Typography variant="body2" data-testid="bil-quote-patient-fixed">
              {t('bil:quotes.patient_selected')}
            </Typography>
          ) : (
            <PatientPicker
              labelKey="bil:quotes.columns.patient"
              value={patient}
              onChange={(value) => {
                setPatient(value);
                setPatientId(value?.id ?? '');
              }}
              required
              testId="bil-quote-patient"
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('bil:packages.codes')}
              value={codesText}
              onChange={(event) => setCodesText(event.target.value)}
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-quote-codes' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="number"
              label={t('bil:quotes.validity_days')}
              value={validityDays}
              onChange={(event) => setValidityDays(event.target.value)}
              slotProps={{ htmlInput: { min: 1, max: 365, 'data-testid': 'bil-quote-validity' } }}
              sx={{ width: 160 }}
            />
          </Stack>
          <TextField label={t('bil:quotes.notes')} value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} slotProps={{ htmlInput: { maxLength: 2000 } }} />
        </Stack>
      </Paper>
      <RadioGroup value={String(recommended)} onChange={(event) => setRecommended(Number(event.target.value))}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          {options.map((option, optionIndex) => (
            <Card key={option.key} variant="outlined" sx={{ flex: 1 }} data-testid="bil-quote-option-editor">
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      label={t('bil:quotes.option_title')}
                      value={option.title}
                      onChange={(event) => setOptions((current) => current.map((item) => (item.key === option.key ? { ...item, title: event.target.value } : item)))}
                      slotProps={{ htmlInput: { maxLength: 100 } }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      aria-label={t('bil:quotes.remove_option')}
                      disabled={options.length === 1}
                      onClick={() => {
                        setOptions((current) => current.filter((item) => item.key !== option.key));
                        setRecommended(0);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                  <FormControlLabel value={String(optionIndex)} control={<Radio size="small" />} label={t('bil:quotes.recommended')} />
                  {option.lines.map((line, lineIndex) => (
                    <Stack key={line.key} direction="row" spacing={1} alignItems="center">
                      {line.kind === 'Service' ? (
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={services.options.map((item) => item.id)}
                          value={line.serviceId}
                          onChange={(_event, value) => updateLine(option.key, line.key, { serviceId: value })}
                          getOptionLabel={(id) => services.name(id)}
                          filterOptions={(items) => items}
                          onInputChange={(_event, value, reason) => {
                            if (reason === 'input') {
                              services.search(value);
                            }
                          }}
                          renderInput={(input) => (
                            <TextField
                              {...input}
                              label={t('bil:quotes.service')}
                              slotProps={{ htmlInput: { ...input.inputProps, 'data-testid': `bil-quote-service-${optionIndex}-${lineIndex}` } }}
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={t('bil:packages.package')}
                          value={line.packageId ?? ''}
                          onChange={(event) => updateLine(option.key, line.key, { packageId: event.target.value })}
                          slotProps={{ htmlInput: { 'data-testid': `bil-quote-package-${optionIndex}-${lineIndex}` } }}
                        >
                          {packageOptions.map((item) => (
                            <MenuItem key={item.packageId} value={item.packageId}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      <TextField
                        size="small"
                        type="number"
                        label={line.kind === 'Service' ? t('bil:quotes.sessions') : t('bil:lines.quantity')}
                        value={line.quantity}
                        onChange={(event) => updateLine(option.key, line.key, { quantity: event.target.value })}
                        slotProps={{ htmlInput: { min: 1, max: 100, 'data-testid': `bil-quote-quantity-${optionIndex}-${lineIndex}` } }}
                        sx={{ width: 96 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={t('bil:quotes.stage')}
                        value={line.stage}
                        onChange={(event) => updateLine(option.key, line.key, { stage: event.target.value })}
                        slotProps={{ htmlInput: { min: 1, max: 20 } }}
                        sx={{ width: 80 }}
                      />
                      <IconButton
                        aria-label={t('bil:quotes.remove_line')}
                        disabled={option.lines.length === 1}
                        onClick={() => setOptions((current) => current.map((item) => (item.key === option.key ? { ...item, lines: item.lines.filter((row) => row.key !== line.key) } : item)))}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  ))}
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => setOptions((current) => current.map((item) => (item.key === option.key ? { ...item, lines: [...item.lines, blankLine()] } : item)))}>
                      + {t('bil:quotes.add_service')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setOptions((current) => current.map((item) => (item.key === option.key ? { ...item, lines: [...item.lines, blankLine('Package')] } : item)))}
                      data-testid={`bil-quote-add-package-${optionIndex}`}
                    >
                      + {t('bil:quotes.add_package')}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </RadioGroup>
      <Stack direction="row" spacing={1}>
        <Button
          disabled={options.length >= MAX_OPTIONS}
          onClick={() =>
            setOptions((current) => [...current, { key: nextKey(), title: t('bil:quotes.option_default', { letter: t(`bil:quotes.letters.${current.length}`) }), lines: [blankLine()] }])
          }
          data-testid="bil-quote-add-option"
        >
          + {t('bil:quotes.add_option')}
        </Button>
        <Button variant="contained" disabled={!valid || create.isPending} onClick={() => create.mutate()} data-testid="bil-quote-create">
          {t('bil:quotes.create')}
        </Button>
      </Stack>
      {create.error ? <ErrorState error={create.error} compact /> : null}
    </Stack>
  );
}

/** جزئیات برآورد و چرخه (FLOW-12). */
export function QuoteDetailPage() {
  const { t } = useTranslation('bil');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [optionId, setOptionId] = useState('');
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [converted, setConverted] = useState<BilConvertQuoteResponse | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [printError, setPrintError] = useState<unknown>();
  const queryKey = bilKeys.quote(session?.tenantId, id);
  const quote = useQuery({
    queryKey,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/quotes/{id}', { params: { path: { id } } })),
    enabled: id !== '',
  });
  const set = (view: BilQuoteView) => {
    queryClient.setQueryData(queryKey, view);
    void queryClient.invalidateQueries({ queryKey: ['billing', session?.tenantId, 'quotes'] });
  };
  const present = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/billing/quotes/{id}:present', { params: { path: { id } } })),
    onSuccess: set,
  });
  const requestSignature = useMutation({
    mutationFn: (option: string) => unwrap(tenantApi.POST('/api/v1/billing/quotes/{id}:request-signature', { params: { path: { id } }, body: { optionId: option } })),
    onSuccess: (view) => {
      set(view);
      setNotice(t('bil:quotes.signature_sent'));
    },
  });
  const sendOtp = useMutation({
    mutationFn: (option: string) => unwrap(tenantApi.POST('/api/v1/billing/quotes/{id}:send-otp', { params: { path: { id } }, body: { optionId: option } })),
    onSuccess: (result) => setOtpId(result.otpId),
  });
  const accept = useMutation({
    mutationFn: (option: string) => unwrap(tenantApi.POST('/api/v1/billing/quotes/{id}:accept', { params: { path: { id } }, body: { optionId: option, otpId: otpId ?? '', code: code.trim() } })),
    onSuccess: (view) => {
      set(view);
      setOtpOpen(false);
      setOtpId(null);
      setCode('');
      setNotice(t('bil:quotes.accepted'));
    },
  });
  const reject = useMutation({
    mutationFn: (reason: string) => unwrap(tenantApi.POST('/api/v1/billing/quotes/{id}:reject', { params: { path: { id } }, body: { reason } })),
    onSuccess: (view) => {
      set(view);
      setRejecting(false);
    },
  });
  const convert = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/billing/quotes/{id}:convert', { params: { path: { id } }, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (result) => {
      set(result.quote);
      setConverted(result);
    },
  });
  const print = async (number: string) => {
    setPrintError(undefined);
    const response = await tenantApi.GET('/api/v1/billing/quotes/{id}/print', { params: { path: { id }, query: { output: 'Pdf' } }, parseAs: 'blob' });
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `${number}.pdf`);
    } else {
      setPrintError(response.error ?? new Error('print'));
    }
  };

  if (quote.isPending) {
    return <LoadingState rows={6} />;
  }
  if (quote.error || !quote.data) {
    return <ErrorState error={quote.error} onRetry={() => void quote.refetch()} />;
  }
  const view = quote.data;
  const can = access.accessMode === 'Full' && access.can('bil.quote.create');
  const selected = optionId || view.pendingOptionId || view.options.find((option) => option.recommended)?.id || view.options[0]?.id || '';
  const choose = (value: string) => setOptionId(value);
  const error = present.error ?? requestSignature.error ?? reject.error ?? convert.error ?? printError;
  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_407}>
      <PageHeader
        titleKey="bil:quotes.detail_title"
        titleValues={{ number: view.number }}
        badges={
          <Stack direction="row" spacing={1} alignItems="center">
            <QuoteStatusChip status={view.status} />
            <Typography variant="body2" color="text.secondary">
              {view.patientName ?? ''} · {t('bil:quotes.valid_until')} <JalaliDate value={view.validUntil} />
            </Typography>
          </Stack>
        }
        actions={
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {can && view.status === 'Draft' ? (
              <Button variant="contained" onClick={() => present.mutate()} disabled={present.isPending} data-testid="bil-quote-present">
                {t('bil:quotes.present')}
              </Button>
            ) : null}
            {view.status !== 'Draft' ? (
              <Button onClick={() => void print(view.number)} data-testid="bil-quote-print">
                {t('bil:quotes.print')}
              </Button>
            ) : null}
            {can && view.status === 'Presented' ? (
              <>
                <Button onClick={() => requestSignature.mutate(selected)} disabled={requestSignature.isPending || !selected} data-testid="bil-quote-request-signature">
                  {t('bil:quotes.request_signature')}
                </Button>
                <Button onClick={() => setOtpOpen(true)} data-testid="bil-quote-accept-otp">
                  {t('bil:quotes.accept_otp')}
                </Button>
                <Button color="error" onClick={() => setRejecting(true)} data-testid="bil-quote-reject">
                  {t('bil:quotes.reject')}
                </Button>
              </>
            ) : null}
            {can && view.status === 'Accepted' ? (
              <Button variant="contained" onClick={() => convert.mutate()} disabled={convert.isPending} data-testid="bil-quote-convert">
                {t('bil:quotes.convert')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      {notice ? (
        <Alert severity="success" onClose={() => setNotice(null)} data-testid="bil-quote-notice">
          {notice}
        </Alert>
      ) : null}
      {view.signatureStatus ? (
        <Typography variant="body2" data-testid="bil-quote-signature-status">
          {t('bil:quotes.signature_status', { status: view.signatureStatus })}
        </Typography>
      ) : null}
      {error ? <ErrorState error={error} compact /> : null}
      {converted ? <ConvertResult result={converted} patientId={view.patientId} /> : null}
      <RadioGroup value={selected} onChange={(event) => choose(event.target.value)}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          {view.options.map((option) => {
            const accepted = view.acceptedOptionId === option.id;
            return (
              <Card key={option.id} variant="outlined" sx={{ flex: 1, borderColor: accepted ? 'success.main' : undefined }} data-testid="bil-quote-option" data-option-id={option.id}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {view.status === 'Presented' ? (
                      <FormControlLabel value={option.id} control={<Radio size="small" />} label={option.title} />
                    ) : (
                      <Typography variant="subtitle1">{option.title}</Typography>
                    )}
                    {option.recommended ? <Chip size="small" color="primary" label={t('bil:quotes.recommended')} /> : null}
                    {accepted ? <Chip size="small" color="success" label={t('bil:quotes.accepted_option')} data-testid="bil-quote-accepted-option" /> : null}
                  </Stack>
                  <Table size="small" aria-label={option.title}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('bil:quotes.item')}</TableCell>
                        <TableCell>{t('bil:quotes.sessions')}</TableCell>
                        <TableCell>{t('bil:quotes.stage')}</TableCell>
                        <TableCell>{t('bil:quotes.line_total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {option.lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.title}</TableCell>
                          <TableCell>{faDigits(num(line.kind === 'Package' ? line.sessions : line.quantity))}</TableCell>
                          <TableCell>{faDigits(num(line.stage))}</TableCell>
                          <TableCell>
                            <Amount money={line.total} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {t('bil:packages.gross')} <Amount money={option.gross} /> · {t('bil:packages.discount')} <Amount money={option.discount} />
                    </Typography>
                    <Typography variant="body2">
                      {t('bil:quotes.insurance')} <Amount money={option.insuranceEstimate} /> · {t('bil:quotes.tax')} <Amount money={option.tax} />
                    </Typography>
                    <Typography variant="subtitle2">
                      {t('bil:quotes.total')} <Amount money={option.total} testId="bil-quote-option-total" />
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </RadioGroup>
      {view.notes ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2">{view.notes}</Typography>
        </Paper>
      ) : null}
      <FormDialog
        open={otpOpen}
        titleKey="bil:quotes.accept_otp"
        onSubmit={() => (otpId ? accept.mutate(selected) : sendOtp.mutate(selected))}
        onClose={() => {
          setOtpOpen(false);
          setOtpId(null);
          setCode('');
          sendOtp.reset();
          accept.reset();
        }}
        isDirty={code !== ''}
        isSubmitting={sendOtp.isPending || accept.isPending}
        submitError={sendOtp.error ?? accept.error}
        submitKey={otpId ? 'bil:quotes.confirm_accept' : 'bil:quotes.send_otp'}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField select label={t('bil:quotes.option')} value={selected} onChange={(event) => choose(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'bil-quote-otp-option' } }}>
            {view.options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.title}
              </MenuItem>
            ))}
          </TextField>
          {otpId ? (
            <>
              <Typography variant="body2" data-testid="bil-quote-otp-sent">
                {t('bil:quotes.otp_sent')}
              </Typography>
              <TextField
                required
                label={t('bil:quotes.otp_code')}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 8, 'data-testid': 'bil-quote-otp-code' } }}
              />
            </>
          ) : (
            <Typography variant="body2">{t('bil:quotes.otp_hint')}</Typography>
          )}
        </Stack>
      </FormDialog>
      <ReasonDialog open={rejecting} titleKey="bil:quotes.reject" busy={reject.isPending} error={reject.error} onConfirm={(reason) => reject.mutate(reason)} onCancel={() => setRejecting(false)} />
    </Stack>
  );
}

function ConvertResult({ result, patientId }: { result: BilConvertQuoteResponse; patientId: string }) {
  const { t } = useTranslation('bil');
  const access = useAccess();
  return (
    <Alert severity="success" data-testid="bil-quote-converted">
      <Typography variant="subtitle2">{t('bil:quotes.converted', { packages: faDigits(result.packageSaleIds.length), locks: faDigits(num(result.priceLocks)) })}</Typography>
      {result.suggestions.length > 0 ? (
        <>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('bil:quotes.suggestions')}
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2 }}>
            {result.suggestions.map((suggestion, index) => (
              <li key={index} data-testid="bil-quote-suggestion">
                {t('bil:quotes.suggestion', { service: suggestion.serviceName ?? '', stage: faDigits(num(suggestion.stage)), sessions: faDigits(num(suggestion.sessions)) })}{' '}
                <JalaliDate value={suggestion.suggestedFrom} />
              </li>
            ))}
          </Stack>
        </>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" component={RouterLink} to={packagesPath(patientId)} data-testid="bil-quote-to-packages">
          {t('bil:packages.title')}
        </Button>
        {result.invoiceIds.length > 0 && access.can('bil.payment.receive') ? (
          <Button size="small" component={RouterLink} to={cashierPatientPath(patientId)} data-testid="bil-quote-to-cashier">
            {t('bil:packages.to_cashier')}
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
}
